import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { OrchestratorService } from '../../services/orchestrator/orchestrator.service';
import { Auth, signOut } from '@angular/fire/auth';
import { Router } from '@angular/router';
import { FileService } from '../../services/file/file.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ERROR_SNACKBAR_CONFIG } from '../../constants';
import { Clipboard, ClipboardModule } from '@angular/cdk/clipboard';
import { BehaviorSubject, Subscription } from 'rxjs';
import { FileMetadata } from '../../models/file-metadata.model';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog } from '@angular/material/dialog';
import { UpdateModelDialogComponent } from './update-model-dialog/update-model-dialog.component';

@Component({
  selector: 'app-admin-console',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    ClipboardModule,
    MatGridListModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './admin-console.component.html',
  styleUrl: './admin-console.component.scss',
})
export class AdminConsoleComponent implements OnInit, OnDestroy {
  form!: FormGroup;
  fileError = '';
  fileRegistrationCompleted = false;
  registeredFileId?: string;
  userEmail!: string;
  loadingSpinnerActiveState = {
    registration: false,
    loadingFiles: false,
    delete: new Map<string, boolean>(),
  };

  registeredModels$: BehaviorSubject<FileMetadata[]> = new BehaviorSubject<
    FileMetadata[]
  >([]);

  private _subs: Subscription[] = [];

  constructor(
    private readonly _orchestrator: OrchestratorService,
    private readonly _auth: Auth,
    private readonly _router: Router,
    private readonly _fileService: FileService,
    private readonly _clipboard: Clipboard,
    private readonly _snackBar: MatSnackBar,
    private readonly _dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this._initForm();
    this._refreshRegisteredModels();
    this.userEmail = this._auth.currentUser!.email!;

    this.registeredModels$.subscribe(() => {});
  }

  ngOnDestroy(): void {
    this._subs.forEach((s) => s.unsubscribe());
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const file = input.files[0];
    this.form.get('file')?.setValue(file);
    this.form.get('file')?.markAsTouched();
    this.form.get('file')?.updateValueAndValidity();

    const sub = this._orchestrator.runComplianceCheckOnFile(file).subscribe({
      next: () => {
        this.fileError = '';
      },
      error: (err) => {
        this.fileError = err.message;
      },
    });
    this._subs.push(sub);
  }

  onSubmit() {
    const modelName = this.form.get('modelName')?.value;
    const modelVersion = this.form.get('modelVersion')?.value;
    const file = this.form.get('file')?.value;

    this.loadingSpinnerActiveState.registration = true;

    if (this.form.invalid) return;

    const sub = this._fileService
      .registerExcelFile(modelName, modelVersion, file)
      .subscribe({
        next: (response: { data: { id: string } }) => {
          this.fileRegistrationCompleted = true;
          this.registeredFileId = response.data.id;
          this._refreshRegisteredModels();

          this.loadingSpinnerActiveState.registration = false;
        },
        error: () => {
          this._snackBar.open(
            "Erreur dans l'enregistrement du modèle. Si le problème persiste, contactez le support.",
            'Fermer',
            ERROR_SNACKBAR_CONFIG
          );
        },
      });
    this._subs.push(sub);
  }

  onClickSignOut(): void {
    signOut(this._auth);
    this._router.navigateByUrl('/login');
  }

  onClickCopyToClipboard(text: string | null) {
    if (text) {
      this._clipboard.copy(text);

      this._snackBar.open(
        "Erreur dans l'enregistrement du modèle. Si le problème persiste, contactez le support.",
        'Fermer'
      );
    }
  }

  onClickDelete(fileId: string) {
    this.loadingSpinnerActiveState.delete.set(fileId, true);

    const sub = this._fileService.deleteExcelFile(fileId).subscribe({
      next: () => {
        this._snackBar.open('Modèle supprimé avec succès !');
        this._refreshRegisteredModels();
        this.loadingSpinnerActiveState.delete.set(fileId, false);
      },
      error: () => {
        this._snackBar.open(
          'Erreur dans la suppression du modèle. Si le problème persiste, contactez le support.',
          'Fermer',
          ERROR_SNACKBAR_CONFIG
        );
        this.loadingSpinnerActiveState.delete.set(fileId, false);
      },
    });
    this._subs.push(sub);
  }

  onClickOpenModel(fileId: string) {
    this._router.navigate(['/dashboard'], { queryParams: { id: fileId } });
  }

  onClickUpdateModel(model: FileMetadata) {
    const dialog = this._dialog.open(UpdateModelDialogComponent, {
      data: { ...model },
    });

    dialog.afterClosed().subscribe((result) => {
      if (result.updated) this._refreshRegisteredModels();
    });
  }

  private _initForm(): void {
    this.form = new FormGroup({
      modelName: new FormControl<string>('', [Validators.required]),
      modelVersion: new FormControl<string>('', [Validators.required]),
      file: new FormControl<File | null>(null, [
        Validators.required,
        this._excelFileValidator.bind(this),
      ]),
    });
  }

  private _excelFileValidator(
    control: AbstractControl
  ): ValidationErrors | null {
    const file = control.value;
    if (!file) return { required: true };

    const allowedTypes = [
      'application/vnd.ms-excel', // .xls
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel.sheet.macroEnabled.12', // .xlsm
    ];

    return file instanceof File && allowedTypes.includes(file.type)
      ? null
      : { invalidFileType: true };
  }

  private _refreshRegisteredModels() {
    this.loadingSpinnerActiveState.loadingFiles = true;

    const sub = this._fileService.getExcelFiles().subscribe({
      next: (response) => {
        this.registeredModels$.next(response.data);

        this.loadingSpinnerActiveState.loadingFiles = false;
      },
    });
    this._subs.push(sub);
  }
}
